import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ChevronRight, 
  ChevronDown, 
  BookOpen, 
  Key, 
  Code, 
  FileText, 
  HelpCircle,
  X
} from 'lucide-react';

const MenuItem = ({ item, level = 0, isActive, hasActiveChild, onClose }) => {
  const [isExpanded, setIsExpanded] = React.useState(hasActiveChild(item) || isActive(item.path));
  const IconComponent = item.icon;
  const isItemActive = isActive(item.path);
  const hasChildren = item.children && item.children.length > 0;

  return (
    <div>
      <div className="flex items-center">
        {hasChildren ? (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`flex items-center w-full px-3 py-2 text-sm rounded-md transition-colors ${
              isItemActive 
                ? 'bg-blue-50 text-blue-700' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            style={{ paddingLeft: `${level * 12 + 12}px` }}
          >
            {IconComponent && <IconComponent className="w-4 h-4 mr-2" />}
            <span className="flex-1 text-left">{item.title}</span>
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
        ) : (
          <Link
            to={item.path}
            onClick={onClose}
            className={`flex items-center w-full px-3 py-2 text-sm rounded-md transition-colors ${
              isItemActive 
                ? 'bg-blue-50 text-blue-700' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            style={{ paddingLeft: `${level * 12 + 12}px` }}
          >
            {IconComponent && <IconComponent className="w-4 h-4 mr-2" />}
            <span className="flex-1 text-left">{item.title}</span>
          </Link>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div className="mt-1">
          {item.children.map(child => (
            <MenuItem key={child.id} item={child} level={level + 1} isActive={isActive} hasActiveChild={hasActiveChild} onClose={onClose} />
          ))}
        </div>
      )}
    </div>
  );
};

const WikiSidebar = ({ currentSlug, onClose }) => {
  const { t } = useTranslation();

  const menuItems = [
    {
      id: 'getting-started',
      title: t('wiki.menu.gettingStarted'),
      icon: BookOpen,
      path: '/wiki/getting-started'
    },
    {
      id: 'authentication',
      title: t('wiki.menu.authentication'),
      icon: Key,
      path: '/wiki/authentication'
    },
    {
      id: 'api-reference',
      title: t('wiki.menu.apiReference'),
      icon: Code,
      path: '/wiki/api-reference',
      children: [
        { id: 'calculate-emissions', title: t('wiki.menu.calculateEmissions'), path: '/wiki/api-reference/calculate-emissions' },
        { id: 'error-codes', title: t('wiki.menu.errorCodes'), path: '/wiki/api-reference/error-codes' },
        { id: 'rate-limits', title: t('wiki.menu.rateLimits'), path: '/wiki/api-reference/rate-limits' }
      ]
    },
    {
      id: 'guides',
      title: t('wiki.menu.guides'),
      icon: FileText,
      path: '/wiki/guides',
      children: [
        { id: 'road-transport', title: t('wiki.menu.roadTransport'), path: '/wiki/guides/road-transport' },
        { id: 'energy-consumption', title: t('wiki.menu.energyConsumption'), path: '/wiki/guides/energy-consumption' },
        { id: 'manufacturing', title: t('wiki.menu.manufacturing'), path: '/wiki/guides/manufacturing' },
        { id: 'ghg-protocol', title: t('wiki.menu.ghgProtocol'), path: '/wiki/guides/ghg-protocol' }
      ]
    },
    {
      id: 'emission-factors',
      title: t('wiki.menu.emissionFactors'),
      icon: FileText,
      path: '/wiki/emission-factors'
    },
    {
      id: 'best-practices',
      title: t('wiki.menu.bestPractices'),
      icon: FileText,
      path: '/wiki/best-practices'
    },
    {
      id: 'error-handling',
      title: t('wiki.menu.errorHandling'),
      icon: FileText,
      path: '/wiki/error-handling'
    },
    {
      id: 'faq',
      title: t('wiki.menu.faq'),
      icon: HelpCircle,
      path: '/wiki/faq'
    }
  ];

  const isActive = (path) => {
    return currentSlug === path.replace('/wiki/', '') || 
           (path !== '/wiki' && currentSlug.startsWith(path.replace('/wiki/', '')));
  };

  const hasActiveChild = (item) => {
    if (!item.children) return false;
    return item.children.some(child => isActive(child.path));
  };


  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">{t('wiki.title')}</h2>
        <button
          onClick={onClose}
          className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4">
        <div className="space-y-1">
          {menuItems.map((item) => (
            <MenuItem key={item.id} item={item} isActive={isActive} hasActiveChild={hasActiveChild} onClose={onClose} />
          ))}
        </div>
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200">
        <div className="text-xs text-gray-500">
          <p>{t('wiki.footer.lastUpdated')}</p>
          <p>{t('wiki.footer.version')}</p>
        </div>
      </div>
    </div>
  );
};

export default WikiSidebar;
